
const embedBridge = (() => {
  const embedded = window.self !== window.top;
  if (embedded) document.documentElement.classList.add("is-embedded");

  let lastHeight = 0;
  let targetOrigin = "*";
  try {
    if (document.referrer) targetOrigin = new URL(document.referrer).origin;
  } catch (error) {
    targetOrigin = "*";
  }

  const sendHeight = () => {
    if (!embedded) return;
    const height = Math.ceil(document.documentElement.scrollHeight);
    if (!height || height === lastHeight) return;
    lastHeight = height;
    window.parent.postMessage({ type: "springcalc:resize", height }, targetOrigin);
  };

  window.addEventListener("load", sendHeight);
  window.addEventListener("resize", sendHeight);
  document.addEventListener("input", () => requestAnimationFrame(sendHeight));
  document.addEventListener("click", () => setTimeout(sendHeight, 80), true);
  if ("ResizeObserver" in window) new ResizeObserver(sendHeight).observe(document.body);

  return { sendHeight };
})();

let calculationMode = null;
let activeBikePreset = null;
let activeBikeVariant = null;
let activeBikeGeometry = null;
let expertCurveSource = "none";

const EFFECTIVE_RIDER_OFFSET_MM = 130;

const quickPresets = {
  xc: { travel: 100, stroke: 40, sag: 28, rearLoad: 57 },
  trail: { travel: 140, stroke: 50, sag: 30, rearLoad: 58 },
  enduro: { travel: 160, stroke: 60, sag: 30, rearLoad: 58 },
  dh: { travel: 200, stroke: 75, sag: 33, rearLoad: 59 },
  custom: { travel: 160, stroke: 60, sag: 30, rearLoad: 58 }
};

const bikePresets = [
  {
    id: "end-megatower-2", discipline: "enduro", brand: "Santa Cruz", model: "Megatower 2", generation: "MY26",
    travel: 165, stroke: 62.5, sag: 30, sagSource: "Enduro-Preset (keine aktuelle Herstellerzahl veröffentlicht)",
    evidence: "qualitative", evidenceLabel: "Hersteller beschreibt High/Low, keine Punktkurve",
    source: "https://www.santacruzbicycles.com/de-de/pages/product-support/megatower-2-my-26",
    geometry: [
      { size: "S", wheelbase: 1206, rearCenter: 436, bbDrop: 26.5 },
      { size: "M", wheelbase: 1236, rearCenter: 437, bbDrop: 26.5 },
      { size: "L", wheelbase: 1266, rearCenter: 440, bbDrop: 26.5, default: true },
      { size: "XL", wheelbase: 1298, rearCenter: 443, bbDrop: 26.5 },
      { size: "XXL", wheelbase: 1333, rearCenter: 447, bbDrop: 26.5 }
    ],
    variants: [
      { label: "High · 165 / 62,5 mm", travel: 165, stroke: 62.5, note: "Hersteller-Startposition" },
      { label: "Low · 165 / 62,5 mm", travel: 165, stroke: 62.5, note: "etwas progressiver" },
      { label: "Long Stroke · 170 / 65 mm", travel: 170, stroke: 65, note: "offiziell freigegeben" }
    ]
  },
  {
    id: "end-nomad-6", discipline: "enduro", brand: "Santa Cruz", model: "Nomad 6", generation: "MY26",
    travel: 170, stroke: 65, sag: 30, sagSource: "Enduro-Preset", evidence: "qualitative",
    evidenceLabel: "Hersteller beschreibt High/Low, keine Punktkurve",
    source: "https://www.santacruzbicycles.com/de-de/pages/product-support/nomad-6-my-26",
    geometry: [
      { size: "S", wheelbase: 1209, rearCenter: 439, bbDrop: 8 },
      { size: "M", wheelbase: 1239, rearCenter: 440, bbDrop: 8 },
      { size: "L", wheelbase: 1269, rearCenter: 443, bbDrop: 8, default: true },
      { size: "XL", wheelbase: 1301, rearCenter: 446, bbDrop: 8 },
      { size: "XXL", wheelbase: 1336, rearCenter: 450, bbDrop: 8 }
    ],
    variants: [
      { label: "High · Hersteller-Startpunkt", travel: 170, stroke: 65 },
      { label: "Low · etwas progressiver", travel: 170, stroke: 65 }
    ]
  },
  { id: "end-specialized-enduro", discipline: "enduro", brand: "Specialized", model: "Enduro", generation: "aktuelle Plattform", travel: 170, stroke: 60, sag: 30, sagSource: "Enduro-Preset", evidence: "specs", evidenceLabel: "Herstellermaße, keine öffentliche Punktkurve", source: "https://www.specialized.com/es/es/s-works-enduro-frameset/p/4276009" },
  {
    id: "end-slash-gen6", discipline: "enduro", brand: "Trek", model: "Slash Gen 6", generation: "Gen 6",
    travel: 170, stroke: 65, sag: 30, sagSource: "Enduro-Preset", evidence: "aggregate",
    evidenceLabel: "20-/25-%-Progression veröffentlicht, keine Punktkurve",
    source: "https://www.trekbikes.com/de/de_DE/FAQ/Slash-Gen6/",
    variants: [
      { label: "25 % Progression · Werkseinstellung", travel: 170, stroke: 65 },
      { label: "20 % Progression · weicher", travel: 170, stroke: 65 }
    ]
  },
  {
    id: "end-strive-cfr", discipline: "enduro", brand: "Canyon", model: "Strive CFR", generation: "aktuelle CFR-Plattform",
    travel: 160, stroke: 65, sag: 30, sagSource: "Enduro-Preset", evidence: "specs",
    evidenceLabel: "Shapeshifter-Modi veröffentlicht, keine Ratio-Punktkurve", source: "https://www.canyon.com/en-us/productpdf/geometry/?pid=3416",
    variants: [
      { label: "Shred · 160 / 65 mm", travel: 160, stroke: 65 },
      { label: "Pedal · nominell 140 mm", travel: 140, stroke: 65, note: "Systemmodus; nur nomineller Durchschnitt" }
    ]
  },
  { id: "end-capra-mk3", discipline: "enduro", brand: "YT", model: "Capra MK3", generation: "MK3", travel: 170, stroke: 65, sag: 30, sagSource: "Enduro-Preset", evidence: "specs", evidenceLabel: "Herstellermaße, keine öffentliche Punktkurve", source: "https://www.yt-industries.com/en-us/Bikes/Enduro-Capra/CORE-4/" },
  {
    id: "end-firebird", discipline: "enduro", brand: "Pivot", model: "Firebird", generation: "MY26",
    travel: 165, stroke: 65, sag: 30, sagSource: "Hersteller: 30 % / 19,5 mm", evidence: "qualitative",
    evidenceLabel: "Linearer/progressiver Flip-Chip, keine Punktkurve", source: "https://www.pivotcycles.com/en/bikes/firebird",
    variants: [
      { label: "Linearer", travel: 165, stroke: 65 },
      { label: "Progressiver", travel: 165, stroke: 65 }
    ]
  },
  {
    id: "end-hd6", discipline: "enduro", brand: "Ibis", model: "HD6", generation: "aktuelle Plattform",
    travel: 165, stroke: 65, sag: 30, sagSource: "Herstellerbereich 25–30 %; Startwert 30 %",
    evidence: "specs", evidenceLabel: "Herstellermaße und Größen-Geometrie; keine öffentliche Punktkurve",
    source: "https://www.ibiscycles.com/bikes/hd6",
    geometry: [
      { size: "S (1)", wheelbase: 1202, rearCenter: 435, bbDrop: 30 },
      { size: "M (2)", wheelbase: 1228, rearCenter: 435, bbDrop: 30 },
      { size: "XM (3)", wheelbase: 1256, rearCenter: 435, bbDrop: 30 },
      { size: "L (4)", wheelbase: 1288, rearCenter: 435, bbDrop: 30, default: true },
      { size: "XL (5)", wheelbase: 1328, rearCenter: 435, bbDrop: 30 }
    ]
  },
  { id: "end-sb165", discipline: "enduro", brand: "Yeti", model: "SB165", generation: "aktuelle Plattform", travel: 165, stroke: 65, sag: 30, sagSource: "Hersteller: 30 % / 19,5 mm", evidence: "qualitative", evidenceLabel: "Progression qualitativ beschrieben, keine Punktkurve", source: "https://yeticycles.com/en-us/bikes/sb165/kits" },
  {
    id: "end-spire", discipline: "enduro", brand: "Transition", model: "Spire", generation: "aktuelle Plattform",
    travel: 170, stroke: 65, sag: 30, sagSource: "Herstellerbereich 28–34 %; Startwert 30 %", evidence: "graph",
    evidenceLabel: "Offizielles Herstellerdiagramm; Digitalisierung möglich", source: "https://www.transitionbikes.com/SupportFiles/Spire%20PDF.pdf",
    variants: [
      { label: "170 / 65 mm", travel: 170, stroke: 65, sag: 30 },
      { label: "160 / 60 mm", travel: 160, stroke: 60, sag: 30 }
    ]
  },
  { id: "end-rallon", discipline: "enduro", brand: "Orbea", model: "Rallon Enduro", generation: "MY26", travel: 170, stroke: 62.5, sag: 30, sagSource: "Enduro-Preset", evidence: "graph", evidenceLabel: "Offizielle Kinematikgrafik; Digitalisierung möglich", source: "https://www.orbea.com/en-ca/m/rallon" },
  {
    id: "end-tyee-61", discipline: "enduro", brand: "PROPAIN", model: "Tyee 6.1", generation: "6.1",
    travel: 160, stroke: 55, sag: 30, sagSource: "Enduro-Preset", evidence: "graph",
    evidenceLabel: "Offizielles Leverage-Diagramm und Größen-Geometrie; Digitalisierung möglich",
    source: "https://media-eu.propain-bikes.com/wp-content/uploads/2026/01/23124530/PROPAIN-Tyee-6-1-Manual-v1.pdf",
    geometry: [
      { size: "XS 27,5", wheelbase: 1151, rearCenter: 430, bbDrop: 7 },
      { size: "S 27,5", wheelbase: 1184, rearCenter: 430, bbDrop: 7 },
      { size: "M 27,5", wheelbase: 1216, rearCenter: 430, bbDrop: 7 },
      { size: "M Mix/29", wheelbase: 1237, rearCenter: 445, bbDrop: 22 },
      { size: "L Mix/29", wheelbase: 1266, rearCenter: 445, bbDrop: 22, default: true },
      { size: "XL Mix/29", wheelbase: 1296, rearCenter: 445, bbDrop: 22 }
    ]
  },
  { id: "end-a170", discipline: "enduro", brand: "Atherton", model: "A.170", generation: "2026", travel: 170, stroke: 65, sag: 30, sagSource: "Enduro-Preset", evidence: "specs", evidenceLabel: "Herstellermaße, keine öffentliche Punktkurve", source: "https://www.athertonbikes.com/a170-1.html" },
  { id: "end-ransom", discipline: "enduro", brand: "SCOTT", model: "Ransom", generation: "aktuelle Plattform", travel: 170, stroke: 65, sag: 30, sagSource: "Enduro-Preset", evidence: "graph", evidenceLabel: "Offizielles Leverage-Diagramm; Digitalisierung möglich", source: "https://www.scott-sports.com/in/en/ransom" },
  { id: "end-jekyll", discipline: "enduro", brand: "Cannondale", model: "Jekyll", generation: "aktuelle Plattform / MY21-Handbuch", travel: 165, stroke: 65, sag: 28, sagSource: "Herstellerbereich 25–30 %; Startwert 28 %", evidence: "specs", evidenceLabel: "Herstellermaße und SAG, keine Punktkurve", source: "https://www.cannondale.com/en-us/bikes/mountain/trail-bikes/jekyll/jekyll-1-c21102u" },
  { id: "end-meta-v5", discipline: "enduro", brand: "COMMENCAL", model: "META V5", generation: "V5", travel: 150, stroke: 55, sag: 28.5, sagSource: "Herstellerbereich 27–30 %; Startwert 28,5 %", evidence: "specs", evidenceLabel: "Herstellermaße und SAG, keine Punktkurve", source: "https://www.commencal.com/us/en/bikes/bikes/enduro/meta%20v5/BT4MTRV5STFEU1XL.html" },

  {
    id: "dh-v108", discipline: "dh", brand: "Santa Cruz", model: "V10.8", generation: "MY27",
    travel: 208, stroke: 75, sag: 22.5, sagSource: "Herstellerbereich 22–23 %; Startwert 22,5 %", evidence: "qualitative",
    evidenceLabel: "High/Mid/Low veröffentlicht, keine Punktkurve", source: "https://www.santacruzbicycles.com/en-gb/pages/product-support/v-10-8-my27",
    geometry: [
      { size: "S · Mid", wheelbase: 1230, rearCenter: 445, bbDrop: 0 },
      { size: "M · Mid", wheelbase: 1275, rearCenter: 450, bbDrop: 0 },
      { size: "L · Mid", wheelbase: 1302, rearCenter: 455, bbDrop: 0, default: true },
      { size: "XL · Mid", wheelbase: 1334, rearCenter: 460, bbDrop: 0 }
    ],
    variants: [
      { label: "Mid · Hersteller-Startpunkt", travel: 208, stroke: 75 },
      { label: "High", travel: 208, stroke: 75 },
      { label: "Low", travel: 208, stroke: 75 }
    ]
  },
  { id: "dh-demo", discipline: "dh", brand: "Specialized", model: "Demo Race", generation: "MY24-Plattform", travel: 200, stroke: 75, sag: 33, sagSource: "DH-Preset", evidence: "specs", evidenceLabel: "Herstellermaße, keine öffentliche Punktkurve", source: "https://www.specialized.com/ch/en/demo-race/p/221249" },
  { id: "dh-supreme-v5", discipline: "dh", brand: "COMMENCAL", model: "SUPREME DH V5", generation: "V5", travel: 220, stroke: 75, sag: 22.5, sagSource: "Herstellerbereich 20–25 %; Startwert 22,5 %", evidence: "specs", evidenceLabel: "Herstellermaße und SAG, keine Punktkurve", source: "https://www.commencal.com/en/commencal-supreme-dh-v5-ride-uk/22SUPV5ES.html" },
  {
    id: "dh-session", discipline: "dh", brand: "Trek", model: "Session", generation: "High-Pivot-Plattform",
    travel: 200, stroke: 72.5, sag: 28.5, sagSource: "Herstellerbereich 27–30 %; Startwert 28,5 %", evidence: "aggregate",
    evidenceLabel: "20-/25-%-Progression veröffentlicht, keine Punktkurve", source: "https://media.trekbikes.com/image/upload/v1694708420/TK_Session_MY22_ServiceManual_EN-US_2022-02-21.pdf",
    variants: [
      { label: "25 % Progression · Werkseinstellung", travel: 200, stroke: 72.5 },
      { label: "20 % Progression", travel: 200, stroke: 72.5 }
    ]
  },
  {
    id: "dh-sender", discipline: "dh", brand: "Canyon", model: "Sender CFR", generation: "aktuelle Generation",
    travel: 200, stroke: 75, sag: 33, sagSource: "DH-Preset", evidence: "aggregate",
    evidenceLabel: "32,7-/37-%-Progression veröffentlicht, keine Punktkurve", source: "https://www.canyon.com/en-ch/mountain-bikes/downhill-bikes/sender/",
    variants: [
      { label: "37 % · progressiver", travel: 200, stroke: 75 },
      { label: "32,7 % · plush", travel: 200, stroke: 75 }
    ]
  },
  { id: "dh-tues", discipline: "dh", brand: "YT", model: "TUES", generation: "MK4", travel: 200, stroke: 75, sag: 33, sagSource: "DH-Preset", evidence: "specs", evidenceLabel: "Herstellermaße, keine öffentliche Punktkurve", source: "https://www.yt-industries.com/en-uk/Bikes/Downhill-Tues/CORE-3-Copy/" },
  { id: "dh-phoenix", discipline: "dh", brand: "Pivot", model: "Phoenix", generation: "V5 / MY26", travel: 210, stroke: 75, sag: 30, sagSource: "Hersteller: 30 % / 22,5 mm", evidence: "qualitative", evidenceLabel: "Progressions-Flip-Chip, keine Punktkurve", source: "https://www.pivotcycles.com/en/bikes/phoenix" },
  { id: "dh-summum", discipline: "dh", brand: "Mondraker", model: "Summum", generation: "MY26", travel: 200, stroke: 75, sag: 33, sagSource: "DH-Preset", evidence: "graph", evidenceLabel: "Offizielle Kinematikdiagramme; Digitalisierung möglich", source: "https://mondraker.com/storage/resources/faq/699dcfa30021d263899590-manual-summum-2026-v1-1.pdf" },
  { id: "dh-glory", discipline: "dh", brand: "Giant", model: "Glory Advanced", generation: "aktuelle Plattform", travel: 200, stroke: 75, sag: 30, sagSource: "Hersteller: 30 %", evidence: "graph", evidenceLabel: "Offizielles Leverage-Diagramm; Digitalisierung möglich", source: "https://www.giant-bicycles.com/global/glory-suspension-science" },
  { id: "dh-rage", discipline: "dh", brand: "PROPAIN", model: "Rage CF", generation: "aktuelle Plattform", travel: 215, stroke: 75, sag: 33, sagSource: "DH-Preset", evidence: "graph", evidenceLabel: "Offizielles Diagramm und 38 % Progression", source: "https://www.propain-bikes.com/en/product/bikes/downhill/rage-cf/" },
  {
    id: "dh-m1", discipline: "dh", brand: "INTENSE", model: "M1", generation: "MY26",
    travel: 203, stroke: 75, sag: 33, sagSource: "DH-Preset", evidence: "aggregate",
    evidenceLabel: "27-/31-%-Progression veröffentlicht, keine Punktkurve", source: "https://intensecycles.com/pages/m1-frame-specifications-and-capabilities",
    variants: [
      { label: "Linear · 203 mm / 27 %", travel: 203, stroke: 75 },
      { label: "Progressiv · 212 mm / 31 %", travel: 212, stroke: 75 }
    ]
  },
  { id: "dh-gambler", discipline: "dh", brand: "SCOTT", model: "Gambler", generation: "aktuelle Plattform", travel: 200, stroke: 75, sag: 33, sagSource: "DH-Preset", evidence: "qualitative", evidenceLabel: "Kinematik verstellbar, keine Punktkurve", source: "https://www.scott-sports.com/be/en/product/scott-gambler-900-rc-hmx-frame-set" },
  { id: "dh-a200", discipline: "dh", brand: "Atherton", model: "A.200", generation: "2026", travel: 200, stroke: 75, sag: 33, sagSource: "DH-Preset", evidence: "specs", evidenceLabel: "Herstellermaße, keine öffentliche Punktkurve", source: "https://www.athertonbikes.com/a200-frame.html" }
];

function setCalculationMode(mode) {
  const modeChanged = calculationMode !== mode;
  calculationMode = mode;
  document.getElementById("result").style.display = "none";

  if (modeChanged) {
    document.getElementById("advancedToggle").checked = false;
    document.getElementById("advancedOptions").style.display = "none";
    document.getElementById("expertToggle").checked = false;
    document.getElementById("expertOptions").style.display = "none";
    document.getElementById("leverageCustom").disabled = false;
    expertCurveSource = "none";
  }
  document.querySelectorAll(".mode-card").forEach((card) => {
    const active = card.dataset.mode === mode;
    card.classList.toggle("active", active);
    card.setAttribute("aria-pressed", active ? "true" : "false");
  });

  document.getElementById("calculatorFields").style.display = "block";
  document.getElementById("quickModePanel").style.display = mode === "quick" ? "block" : "none";
  document.getElementById("bikeModePanel").style.display = mode === "bike" ? "block" : "none";
  document.getElementById("travelField").style.display = mode === "standard" ? "block" : "none";
  document.getElementById("strokeField").style.display = mode === "standard" ? "block" : "none";
  document.getElementById("bikeTypeField").style.display = mode === "bike" ? "none" : "block";
  document.getElementById("sagField").style.display = mode === "quick" ? "none" : "block";
  document.getElementById("advancedControl").style.display = mode === "quick" ? "none" : "flex";

  if (mode === "quick") {
    document.getElementById("sagPercent").value = "";
    applyQuickPreset();
  } else if (mode === "bike" && modeChanged) {
    populateBikePresetOptions();
  }

  syncModeUx();

  const submitButton = document.querySelector('#springForm button[type="submit"]');
  submitButton.textContent = mode === "quick" ? "Schnellcheck berechnen" : mode === "bike" ? "Bike-Preset berechnen" : "Federrate berechnen";
}

function syncModeUx() {
  if (!calculationMode) return;
  const bikeMode = calculationMode === "bike";
  const expertEnabled = document.getElementById("expertToggle").checked;

  document.getElementById("advancedLabel").textContent = bikeMode
    ? "Messwerte & Setup verfeinern"
    : "Erweiterte Optionen";
  document.getElementById("sagLabel").textContent = bikeMode ? "Dämpfer-SAG des Bike-Presets (%)" : "Gewünschter Dämpfer-SAG (%)";
  document.getElementById("sagHelp").textContent = bikeMode
    ? "Prozent des Dämpferhubs. Vom Bike-Preset übernommen und bei Bedarf bewusst anpassbar."
    : "Prozent des Dämpferhubs. Optional leer lassen, um das Preset des Bike-Typs zu verwenden.";
  document.getElementById("leverageCustomField").style.display = bikeMode || expertEnabled ? "none" : "block";
  document.getElementById("expertToggleControl").style.display = bikeMode && !expertEnabled ? "none" : "flex";
  document.getElementById("bikePresetLockNotice").style.display = bikeMode && !expertEnabled ? "block" : "none";
  document.getElementById("expertModeContext").style.display = bikeMode && expertEnabled ? "block" : "none";
  syncGeometryInputs();
}

function syncGeometryInputs() {
  const bikeMode = calculationMode === "bike";
  const geometryFields = [
    ["wheelbaseMm", "wheelbase"],
    ["chainstayMm", "rearCenter"],
    ["bbDropMm", "bbDrop"]
  ];

  geometryFields.forEach(([fieldId, geometryKey]) => {
    const input = document.getElementById(fieldId);
    if (bikeMode) {
      input.value = activeBikeGeometry && Number.isFinite(activeBikeGeometry[geometryKey])
        ? activeBikeGeometry[geometryKey]
        : "";
      input.readOnly = true;
      input.dataset.fromBikePreset = "true";
    } else {
      if (input.dataset.fromBikePreset === "true") input.value = "";
      input.readOnly = false;
      delete input.dataset.fromBikePreset;
    }
  });

  document.getElementById("geometryHelp").textContent = bikeMode
    ? activeBikeGeometry
      ? `Geometrie der gewählten Größe ${activeBikeGeometry.size} ist gesperrt. Für eine echte Messung haben die beiden Waagen Vorrang.`
      : "Für dieses Bike ist keine Größen-Geometrie hinterlegt. Der Rechner verwendet die kalibrierte Lastverteilung; zwei Waagen können sie ersetzen."
    : "Radstand und Rear Center manuell eingeben. Der effektive Fahrer-Offset ist mit Herstellerwerten kalibriert.";
}

function applyQuickPreset() {
  if (calculationMode !== "quick") return;
  const bikeType = document.getElementById("bikeType").value;
  const preset = quickPresets[bikeType] || quickPresets.custom;
  document.getElementById("travelMm").value = preset.travel;
  document.getElementById("strokeMm").value = preset.stroke;
  document.getElementById("leverageCustom").value = "";
  document.getElementById("quickSummary").innerHTML = `
    Verwendetes Preset: <strong>${preset.travel} mm Federweg · ${preset.stroke} mm Dämpferhub · ${preset.sag} % SAG</strong><br>
    Kalibrierte effektive Hinterradlast: ${preset.rearLoad} %. Das Ergebnis ist bewusst nur eine grobe Orientierung.
  `;
}

function populateBikePresetOptions() {
  const discipline = document.getElementById("bikeDisciplineSelect").value;
  const select = document.getElementById("bikePresetSelect");
  const matching = bikePresets
    .filter((preset) => preset.discipline === discipline)
    .sort((a, b) => `${a.brand} ${a.model}`.localeCompare(`${b.brand} ${b.model}`, "de"));

  select.innerHTML = '<option value="">Bitte auswählen</option>' + matching
    .map((preset) => `<option value="${preset.id}">${preset.brand} ${preset.model} · ${preset.generation}</option>`)
    .join("");
  activeBikePreset = null;
  activeBikeVariant = null;
  activeBikeGeometry = null;
  resetBikeVariantSelect();
  resetBikeSizeSelect();
  document.getElementById("bikePresetSummary").innerHTML = "Wähle dein Bike. Danach werden Federweg, Dämpferhub, SAG und Datenstatus übernommen.";
}

function resetBikeVariantSelect() {
  const variantSelect = document.getElementById("bikeVariantSelect");
  variantSelect.innerHTML = '<option value="">Standard</option>';
  variantSelect.disabled = true;
}

function resetBikeSizeSelect() {
  const sizeSelect = document.getElementById("bikeSizeSelect");
  sizeSelect.innerHTML = '<option value="">Geometrie nicht hinterlegt</option>';
  sizeSelect.disabled = true;
  document.getElementById("bikeSizeHelp").textContent = "Für dieses Preset wird die kalibrierte Lastverteilung verwendet.";
  activeBikeGeometry = null;
  syncGeometryInputs();
}

function populateBikeSizeOptions() {
  resetBikeSizeSelect();
  if (!activeBikePreset || !Array.isArray(activeBikePreset.geometry) || activeBikePreset.geometry.length === 0) return;

  const sizeSelect = document.getElementById("bikeSizeSelect");
  sizeSelect.innerHTML = activeBikePreset.geometry
    .map((geometry, index) => `<option value="${index}"${geometry.default ? " selected" : ""}>${geometry.size}</option>`)
    .join("");
  sizeSelect.disabled = false;
  const defaultIndex = Math.max(0, activeBikePreset.geometry.findIndex((geometry) => geometry.default));
  sizeSelect.value = String(defaultIndex);
  activeBikeGeometry = activeBikePreset.geometry[defaultIndex];
  document.getElementById("bikeSizeHelp").textContent = "Radstand, Rear Center und BB-Drop werden automatisch übernommen.";
  syncGeometryInputs();
}

function applyBikeSize() {
  if (!activeBikePreset || !Array.isArray(activeBikePreset.geometry)) return;
  const sizeIndex = Number(document.getElementById("bikeSizeSelect").value);
  activeBikeGeometry = activeBikePreset.geometry[sizeIndex] || activeBikePreset.geometry[0] || null;
  syncGeometryInputs();
  applyBikeVariant();
}

function applyBikePreset() {
  const presetId = document.getElementById("bikePresetSelect").value;
  activeBikePreset = bikePresets.find((preset) => preset.id === presetId) || null;
  activeBikeVariant = null;
  activeBikeGeometry = null;
  resetBikeVariantSelect();
  resetBikeSizeSelect();

  if (!activeBikePreset) {
    document.getElementById("bikePresetSummary").innerHTML = "Wähle dein Bike. Danach werden Federweg, Dämpferhub, SAG und Datenstatus übernommen.";
    return;
  }

  const variantSelect = document.getElementById("bikeVariantSelect");
  if (activeBikePreset.variants && activeBikePreset.variants.length > 0) {
    variantSelect.innerHTML = activeBikePreset.variants
      .map((variant, index) => `<option value="${index}">${variant.label}</option>`)
      .join("");
    variantSelect.disabled = false;
    activeBikeVariant = activeBikePreset.variants[0];
  }
  populateBikeSizeOptions();
  applyBikeVariant();
}

function applyBikeVariant() {
  if (!activeBikePreset) return;
  const variantSelect = document.getElementById("bikeVariantSelect");
  if (!variantSelect.disabled && activeBikePreset.variants) {
    activeBikeVariant = activeBikePreset.variants[Number(variantSelect.value)] || activeBikePreset.variants[0];
  }

  const selected = activeBikeVariant || activeBikePreset;
  const sag = Number.isFinite(selected.sag) ? selected.sag : activeBikePreset.sag;
  document.getElementById("travelMm").value = selected.travel;
  document.getElementById("strokeMm").value = selected.stroke;
  document.getElementById("sagPercent").value = sag;
  document.getElementById("bikeType").value = activeBikePreset.discipline;
  document.getElementById("leverageCustom").value = "";

  const curveRows = document.getElementById("ratioCurveRows");
  curveRows.innerHTML = "";
  curveRows.style.display = "none";
  document.getElementById("ratioCurveText").value = "";
  expertCurveSource = "none";
  if (document.getElementById("expertToggle").checked) generateRatioCurve();

  const evidenceClass = activeBikePreset.evidence === "graph" ? "quality-badge" : "quality-badge";
  const expertCopy = activeBikePreset.evidence === "graph"
    ? "Herstellerdiagramm vorhanden – im Expertenmodus kann die digitalisierte Punktreihe übernommen werden."
    : "Keine öffentliche Punktreihe hinterlegt – eigene Kurvendaten können im Expertenmodus verwendet werden.";
  const variantNote = selected.note ? `<br><small>Variante: ${selected.note}</small>` : "";
  const geometrySpecs = activeBikeGeometry
    ? `
      <span class="spec-pill">Größe ${activeBikeGeometry.size}</span>
      <span class="spec-pill">${activeBikeGeometry.wheelbase} mm Radstand</span>
      <span class="spec-pill">${activeBikeGeometry.rearCenter} mm Rear Center</span>`
    : '<span class="spec-pill">Kalibrierte Lastverteilung</span>';

  document.getElementById("bikePresetSummary").innerHTML = `
    <div class="preset-title">
      <strong>${activeBikePreset.brand} ${activeBikePreset.model}</strong>
      <span class="preset-generation">${activeBikePreset.generation}</span>
    </div>
    <div class="preset-specs">
      <span class="spec-pill">${selected.travel} mm Federweg</span>
      <span class="spec-pill">${selected.stroke} mm Dämpferhub</span>
      <span class="locked-value">Ø Ratio ${(selected.travel / selected.stroke).toFixed(3)}</span>
      <span class="spec-pill">${sag} % SAG</span>
      ${geometrySpecs}
    </div>
    ${variantNote}
    <button type="button" class="expert-entry" onclick="openExpertMode('bike')">Mit Ratio-Kurve genauer berechnen →</button>
    <details class="preset-details">
      <summary>Datenbasis &amp; Genauigkeit ansehen</summary>
      <div class="preset-details-body">
        <span class="${evidenceClass}">${activeBikePreset.evidenceLabel}</span><br><br>
        SAG-Quelle: ${activeBikePreset.sagSource}<br>
        ${expertCopy} <a href="${activeBikePreset.source}" target="_blank" rel="noopener">Herstellerquelle öffnen</a>
      </div>
    </details>
  `;
}

function openExpertMode(source) {
  if (!calculationMode || calculationMode === "quick") {
    setCalculationMode("standard");
  }
  if (calculationMode === "bike" && !activeBikePreset) {
    document.getElementById("bikePresetSummary").innerHTML = "<strong>Bitte zuerst ein Bike auswählen.</strong> Danach kann der Expertenmodus mit den passenden Maßen geöffnet werden.";
    document.getElementById("bikePresetSelect").focus();
    return;
  }

  document.getElementById("advancedToggle").checked = true;
  toggleAdvanced();
  document.getElementById("expertToggle").checked = true;
  toggleExpertMode();

  if (calculationMode === "bike" && activeBikePreset && activeBikePreset.evidence === "graph") {
    setExpertSummary("Herstellerdiagramm ist nachgewiesen, aber noch keine digitalisierte Punktreihe hinterlegt. Die aktuell erzeugte konstante Kurve ist nur der Durchschnitt und muss für eine Expertenberechnung ersetzt werden.");
  }
  document.getElementById("expertOptions").scrollIntoView({ behavior: "smooth", block: "start" });
}

document.getElementById("springForm").addEventListener("submit", function (event) {
  event.preventDefault();
  calculateSpring();
});

document.getElementById("ratioCurveRows").addEventListener("input", function () {
  expertCurveSource = "manual";
  updateExpertPreview();
});
document.getElementById("travelMm").addEventListener("input", function () {
  if (document.getElementById("expertToggle").checked) updateExpertPreview();
});
document.getElementById("strokeMm").addEventListener("input", function () {
  if (document.getElementById("expertToggle").checked && getRatioCurvePoints().length > 0) {
    updateExpertPreview();
  }
});
document.getElementById("strokeMm").addEventListener("change", function () {
  if (!document.getElementById("expertToggle").checked) return;
  if (getRatioCurvePoints().length === 0) {
    generateRatioCurve();
  } else {
    updateExpertPreview();
  }
});

function toggleAdvanced() {
  document.getElementById("advancedOptions").style.display =
    document.getElementById("advancedToggle").checked ? "block" : "none";
  syncModeUx();
}

function toggleExpertMode() {
  const enabled = document.getElementById("expertToggle").checked;
  document.getElementById("expertOptions").style.display = enabled ? "block" : "none";
  document.getElementById("leverageCustom").disabled = enabled;
  syncModeUx();

  if (enabled) {
    if (getRatioCurvePoints().length === 0) {
      generateRatioCurve();
    } else {
      updateExpertPreview();
    }
  }
}

function getExpectedShockPositions(strokeMm) {
  if (!Number.isFinite(strokeMm) || strokeMm <= 0) return [];

  const positions = [];
  const wholeMillimeters = Math.floor(strokeMm + 1e-9);
  for (let position = 0; position <= wholeMillimeters; position += 1) {
    positions.push(position);
  }
  if (Math.abs(positions[positions.length - 1] - strokeMm) > 1e-6) {
    positions.push(strokeMm);
  }
  return positions;
}

function formatValue(value, digits = 2) {
  return value.toLocaleString("de-CH", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  });
}

function renderRatioCurve(positions, ratios) {
  const rows = positions.map((position, index) => `
    <tr>
      <td>${formatValue(position, Number.isInteger(position) ? 0 : 1)} mm</td>
      <td>
        <input
          class="ratio-value"
          type="number"
          min="1"
          max="5"
          step="0.001"
          data-position="${position}"
          value="${ratios[index].toFixed(3)}"
          aria-label="Leverage Ratio bei ${position} Millimeter Dämpferhub"
        />
      </td>
    </tr>
  `).join("");

  const container = document.getElementById("ratioCurveRows");
  container.innerHTML = `
    <table class="ratio-table">
      <thead>
        <tr><th>Dämpferposition</th><th>Ratio Hinterrad : Dämpfer</th></tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>
  `;
  container.style.display = "block";
}

function generateRatioCurve() {
  const strokeMm = parseFloat(document.getElementById("strokeMm").value);
  const travelMm = parseFloat(document.getElementById("travelMm").value);
  const customAverage = parseFloat(document.getElementById("leverageCustom").value);

  if (!Number.isFinite(strokeMm) || strokeMm <= 0 || !Number.isFinite(travelMm) || travelMm <= 0) {
    setExpertSummary("Bitte zuerst Hinterbau-Federweg und Dämpferhub eingeben.", true);
    clearRatioChart();
    return;
  }

  const averageRatio = Number.isFinite(customAverage) ? customAverage : travelMm / strokeMm;
  const positions = getExpectedShockPositions(strokeMm);
  document.getElementById("ratioCurveText").value = "";
  renderRatioCurve(positions, positions.map(() => averageRatio));
  expertCurveSource = "average";
  updateExpertPreview();
}

function applyRatioCurveText() {
  const strokeMm = parseFloat(document.getElementById("strokeMm").value);
  const positions = getExpectedShockPositions(strokeMm);
  const lines = document.getElementById("ratioCurveText").value
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (positions.length === 0) {
    setExpertSummary("Bitte zuerst einen gültigen Dämpferhub eingeben.", true);
    return;
  }
  if (lines.length !== positions.length) {
    setExpertSummary(`Für ${formatValue(strokeMm, Number.isInteger(strokeMm) ? 0 : 1)} mm Hub werden ${positions.length} Werte benötigt; erkannt wurden ${lines.length}.`, true);
    return;
  }

  const ratios = [];
  for (let index = 0; index < lines.length; index += 1) {
    const parts = lines[index].split(/[;\t]/).map((part) => part.trim()).filter(Boolean);
    const normalizedParts = parts.length === 1 ? lines[index].split(/\s+/) : parts;

    let position = positions[index];
    let ratioText;
    if (normalizedParts.length === 1) {
      ratioText = normalizedParts[0];
    } else if (normalizedParts.length === 2) {
      position = Number(normalizedParts[0].replace(",", "."));
      ratioText = normalizedParts[1];
    } else {
      setExpertSummary(`Zeile ${index + 1} hat kein gültiges Format.`, true);
      return;
    }

    const ratio = Number(ratioText.replace(",", "."));
    if (!Number.isFinite(position) || Math.abs(position - positions[index]) > 0.01) {
      setExpertSummary(`Zeile ${index + 1}: Erwartete Dämpferposition ${positions[index]} mm.`, true);
      return;
    }
    if (!Number.isFinite(ratio)) {
      setExpertSummary(`Zeile ${index + 1}: Ratio ist keine gültige Zahl.`, true);
      return;
    }
    ratios.push(ratio);
  }

  renderRatioCurve(positions, ratios);
  expertCurveSource = "manual";
  updateExpertPreview();
}

function getRatioCurvePoints() {
  return Array.from(document.querySelectorAll(".ratio-value")).map((input) => ({
    shockMm: Number(input.dataset.position),
    ratio: Number(input.value)
  }));
}

function integrateRatioCurve(points) {
  const integrated = [{ ...points[0], wheelMm: 0 }];
  let wheelMm = 0;

  for (let index = 1; index < points.length; index += 1) {
    const previous = points[index - 1];
    const current = points[index];
    const deltaShock = current.shockMm - previous.shockMm;
    wheelMm += (previous.ratio + current.ratio) / 2 * deltaShock;
    integrated.push({ ...current, wheelMm });
  }
  return integrated;
}

function analyzeExpertCurve(travelMm, strokeMm) {
  const errors = [];
  const warnings = [];
  const points = getRatioCurvePoints();
  const expectedPositions = getExpectedShockPositions(strokeMm);

  if (points.length !== expectedPositions.length) {
    errors.push("Die Ratio-Kurve passt nicht zum aktuellen Dämpferhub. Bitte die Kurve neu erzeugen oder übernehmen.");
    return { errors, warnings };
  }

  points.forEach((point, index) => {
    if (Math.abs(point.shockMm - expectedPositions[index]) > 0.01) {
      errors.push(`Tabellenpunkt ${index + 1} hat eine unerwartete Dämpferposition.`);
    }
    if (!Number.isFinite(point.ratio) || point.ratio < 1 || point.ratio > 5) {
      errors.push(`Ratio bei ${point.shockMm} mm muss zwischen 1.0 und 5.0 liegen.`);
    }
  });

  if (errors.length > 0) return { errors, warnings };

  const integrated = integrateRatioCurve(points);
  const integratedTravelMm = integrated[integrated.length - 1].wheelMm;
  const differenceMm = integratedTravelMm - travelMm;
  const toleranceMm = Math.max(1, travelMm * 0.01);

  if (Math.abs(differenceMm) > toleranceMm) {
    warnings.push(`Die Kurve ergibt ${formatValue(integratedTravelMm, 1)} mm Hinterradweg und weicht damit um ${formatValue(differenceMm, 1)} mm vom Nennfederweg ab.`);
  }
  if (points.some((point) => point.ratio < 1.5 || point.ratio > 3.5)) {
    warnings.push("Mindestens ein Ratio-Wert liegt außerhalb des typischen Bereichs von 1.5 bis 3.5.");
  }
  if (points.some((point, index) => index > 0 && Math.abs(point.ratio - points[index - 1].ratio) > 0.35)) {
    warnings.push("Die Ratio-Kurve enthält mindestens einen ungewöhnlich großen Sprung zwischen benachbarten Punkten.");
  }

  return { errors, warnings, points, integrated, integratedTravelMm, differenceMm };
}

function interpolateCurveAtWheelTravel(integrated, targetWheelMm) {
  for (let index = 1; index < integrated.length; index += 1) {
    const previous = integrated[index - 1];
    const current = integrated[index];
    if (targetWheelMm <= current.wheelMm) {
      const wheelSpan = current.wheelMm - previous.wheelMm;
      const fraction = wheelSpan > 0 ? (targetWheelMm - previous.wheelMm) / wheelSpan : 0;
      return {
        shockMm: previous.shockMm + (current.shockMm - previous.shockMm) * fraction,
        ratio: previous.ratio + (current.ratio - previous.ratio) * fraction
      };
    }
  }
  return null;
}

function interpolateCurveAtShockPosition(integrated, targetShockMm) {
  for (let index = 1; index < integrated.length; index += 1) {
    const previous = integrated[index - 1];
    const current = integrated[index];
    if (targetShockMm <= current.shockMm) {
      const shockSpan = current.shockMm - previous.shockMm;
      const fraction = shockSpan > 0 ? (targetShockMm - previous.shockMm) / shockSpan : 0;
      return {
        shockMm: targetShockMm,
        wheelMm: previous.wheelMm + (current.wheelMm - previous.wheelMm) * fraction,
        ratio: previous.ratio + (current.ratio - previous.ratio) * fraction
      };
    }
  }
  return null;
}

function setExpertSummary(message, isError = false) {
  const summary = document.getElementById("expertSummary");
  summary.className = `expert-summary${isError ? " error" : ""}`;
  summary.innerHTML = message;
}

function updateExpertPreview() {
  const travelMm = parseFloat(document.getElementById("travelMm").value);
  const strokeMm = parseFloat(document.getElementById("strokeMm").value);
  if (!Number.isFinite(travelMm) || !Number.isFinite(strokeMm)) {
    setExpertSummary("Bitte zuerst Hinterbau-Federweg und Dämpferhub eingeben.", true);
    clearRatioChart();
    return;
  }

  const analysis = analyzeExpertCurve(travelMm, strokeMm);
  if (analysis.errors.length > 0) {
    setExpertSummary(analysis.errors.join(" "), true);
    clearRatioChart();
    return;
  }

  const warningHtml = analysis.warnings.length
    ? `<ul class="messages">${analysis.warnings.map((warning) => `<li><small>${warning}</small></li>`).join("")}</ul>`
    : "";
  setExpertSummary(`
    <strong>Integrierter Hinterradweg:</strong> ${formatValue(analysis.integratedTravelMm, 1)} mm<br>
    <strong>Ratio Start → Ende:</strong> ${formatValue(analysis.points[0].ratio, 3)} → ${formatValue(analysis.points[analysis.points.length - 1].ratio, 3)}
    ${warningHtml}
  `);
  drawRatioChart(analysis.integrated);
}

function clearRatioChart() {
  const canvas = document.getElementById("ratioChart");
  const context = canvas.getContext("2d");
  context.clearRect(0, 0, canvas.width, canvas.height);
}

function drawRatioChart(integrated) {
  const canvas = document.getElementById("ratioChart");
  const displayWidth = Math.max(320, Math.round(canvas.getBoundingClientRect().width || 640));
  const displayHeight = 260;
  const pixelRatio = window.devicePixelRatio || 1;
  canvas.width = displayWidth * pixelRatio;
  canvas.height = displayHeight * pixelRatio;

  const context = canvas.getContext("2d");
  context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
  context.clearRect(0, 0, displayWidth, displayHeight);

  const padding = { left: 46, right: 55, top: 28, bottom: 34 };
  const plotWidth = displayWidth - padding.left - padding.right;
  const plotHeight = displayHeight - padding.top - padding.bottom;
  const maxShock = integrated[integrated.length - 1].shockMm;
  const maxWheel = integrated[integrated.length - 1].wheelMm;
  const ratios = integrated.map((point) => point.ratio);
  let minRatio = Math.min(...ratios);
  let maxRatio = Math.max(...ratios);
  const ratioPadding = Math.max(0.1, (maxRatio - minRatio) * 0.12);
  minRatio -= ratioPadding;
  maxRatio += ratioPadding;

  const x = (shockMm) => padding.left + shockMm / maxShock * plotWidth;
  const yRatio = (ratio) => padding.top + (maxRatio - ratio) / (maxRatio - minRatio) * plotHeight;
  const yWheel = (wheelMm) => padding.top + (1 - wheelMm / maxWheel) * plotHeight;

  context.font = "12px Questrial, sans-serif";
  context.lineWidth = 1;
  context.strokeStyle = "#e5e7eb";
  context.fillStyle = "#6b7280";
  for (let step = 0; step <= 4; step += 1) {
    const y = padding.top + step / 4 * plotHeight;
    context.beginPath();
    context.moveTo(padding.left, y);
    context.lineTo(displayWidth - padding.right, y);
    context.stroke();
    const ratioLabel = maxRatio - step / 4 * (maxRatio - minRatio);
    const wheelLabel = maxWheel * (1 - step / 4);
    context.fillText(ratioLabel.toFixed(2), 4, y + 4);
    context.fillText(`${wheelLabel.toFixed(0)} mm`, displayWidth - padding.right + 7, y + 4);
  }

  context.fillText("0", padding.left - 3, displayHeight - 10);
  context.fillText(`${formatValue(maxShock, Number.isInteger(maxShock) ? 0 : 1)} mm Hub`, displayWidth - padding.right - 48, displayHeight - 10);

  context.strokeStyle = "#2563eb";
  context.lineWidth = 2;
  context.beginPath();
  integrated.forEach((point, index) => {
    const pointX = x(point.shockMm);
    const pointY = yRatio(point.ratio);
    if (index === 0) context.moveTo(pointX, pointY);
    else context.lineTo(pointX, pointY);
  });
  context.stroke();

  context.strokeStyle = "#059669";
  context.beginPath();
  integrated.forEach((point, index) => {
    const pointX = x(point.shockMm);
    const pointY = yWheel(point.wheelMm);
    if (index === 0) context.moveTo(pointX, pointY);
    else context.lineTo(pointX, pointY);
  });
  context.stroke();

  context.fillStyle = "#2563eb";
  context.fillRect(padding.left, 8, 18, 3);
  context.fillStyle = "#374151";
  context.fillText("Leverage Ratio", padding.left + 24, 13);
  context.fillStyle = "#059669";
  context.fillRect(padding.left + 135, 8, 18, 3);
  context.fillStyle = "#374151";
  context.fillText("Hinterradposition", padding.left + 159, 13);
}

function calculateGeometryLoad(riderWeightKg, bikeWeightKg, wheelbaseMm, rearCenterMm, bbDropMm, riderOffsetMm) {
  const horizontalRearCenterMm = Math.sqrt(Math.max(0, rearCenterMm ** 2 - bbDropMm ** 2));
  const riderRearShare = 1 - (horizontalRearCenterMm + riderOffsetMm) / wheelbaseMm;
  const bikeRearShare = 1 - horizontalRearCenterMm / wheelbaseMm;
  const rearEquivalentMassKg = riderWeightKg * riderRearShare + bikeWeightKg * bikeRearShare;
  const totalWeightKg = riderWeightKg + bikeWeightKg;

  return {
    horizontalRearCenterMm,
    riderRearShare,
    bikeRearShare,
    rearEquivalentMassKg,
    rearShare: rearEquivalentMassKg / totalWeightKg,
    cgFromRearMm: wheelbaseMm * (1 - rearEquivalentMassKg / totalWeightKg),
    cgFromFrontMm: wheelbaseMm * (rearEquivalentMassKg / totalWeightKg)
  };
}

function calculateSpring() {
  const weightKg = parseFloat(document.getElementById('weightKg').value);
  const bikeWeightKg = parseFloat(document.getElementById('bikeWeightKg').value);
  const travelMm = parseFloat(document.getElementById('travelMm').value);
  const strokeMm = parseFloat(document.getElementById('strokeMm').value);
  const bikeType = document.getElementById('bikeType').value;
  const sagInput = parseFloat(document.getElementById('sagPercent').value);

  const leverageCustom = parseFloat(document.getElementById('leverageCustom').value);
  const frontScaleKg = parseFloat(document.getElementById('frontScaleKg').value);
  const rearScaleKg = parseFloat(document.getElementById('rearScaleKg').value);
  const wheelbaseMm = parseFloat(document.getElementById('wheelbaseMm').value);
  const chainstayMm = parseFloat(document.getElementById('chainstayMm').value);
  const bbDropInput = parseFloat(document.getElementById('bbDropMm').value);
  const riderOffsetInput = parseFloat(document.getElementById('riderOffsetMm').value);
  const preloadInput = parseFloat(document.getElementById('preloadMm').value);
  const useAdvanced = document.getElementById('advancedToggle').checked;
  const useExpert = useAdvanced && document.getElementById('expertToggle').checked;

  const resultDiv = document.getElementById('result');

  const warnings = [];
  const errors = [];

  const isValidNumber = (n) => Number.isFinite(n);
  const showResult = (html, type = "success") => {
    resultDiv.style.display = "block";
    resultDiv.className = `result ${type}`;
    resultDiv.innerHTML = html;
  };

  if (!calculationMode) {
    showResult("<strong>Bitte zuerst Schnellcheck, Standard oder Bike auswählen.</strong>", "error");
    return;
  }
  if (calculationMode === "bike" && !activeBikePreset) {
    showResult("<strong>Bitte zuerst ein Bike-Preset auswählen.</strong>", "error");
    document.getElementById("bikePresetSelect").focus();
    return;
  }

  if (!isValidNumber(weightKg) || !isValidNumber(bikeWeightKg) || !isValidNumber(travelMm) || !isValidNumber(strokeMm)) {
    showResult("<strong>Bitte alle Pflichtfelder ausfüllen.</strong>", "error");
    return;
  }

  if (weightKg < 10 || weightKg > 250) {
    errors.push("Das Fahrergewicht muss zwischen 10 und 250 kg liegen.");
  }
  if (bikeWeightKg < 5 || bikeWeightKg > 50) {
    errors.push("Das Bike-Gewicht muss zwischen 5 und 50 kg liegen.");
  }
  if (travelMm < 40 || travelMm > 250) {
    errors.push("Der Hinterbau-Federweg muss zwischen 40 und 250 mm liegen.");
  }
  if (strokeMm < 20 || strokeMm > 100) {
    errors.push("Der Dämpferhub muss zwischen 20 und 100 mm liegen.");
  }
  const riderOffsetMm = useAdvanced && isValidNumber(riderOffsetInput) ? riderOffsetInput : EFFECTIVE_RIDER_OFFSET_MM;
  const preloadMm = useAdvanced && isValidNumber(preloadInput) ? preloadInput : 0;
  if (riderOffsetMm < 0 || riderOffsetMm > 350) {
    errors.push("Der effektive Fahrer-Offset muss zwischen 0 und 350 mm liegen.");
  }
  if (preloadMm < 0 || preloadMm > 10) {
    errors.push("Die wirksame Federvorspannung muss zwischen 0 und 10 mm liegen.");
  } else if (preloadMm > 5) {
    warnings.push("Mehr als 5 mm Vorspannung sind nur zulässig, wenn der Dämpferhersteller dies ausdrücklich freigibt.");
  }
  if (errors.length > 0) {
    showResult(`<strong>${errors.join(" ")}</strong>`, "error");
    return;
  }

  const totalWeightKg = weightKg + bikeWeightKg;

  // SAG Presets
  let defaultSag = {
    xc: 28,
    trail: 30,
    enduro: 30,
    dh: 33,
    custom: 30
  }[bikeType];

  const bikeTypeName = {
    xc: "XC / Marathon",
    trail: "Trail / All Mountain",
    enduro: "Enduro",
    dh: "Downhill / Park",
    custom: "Custom"
  }[bikeType];

  const usesSagPreset = !isValidNumber(sagInput);
  const sagPercent = usesSagPreset ? defaultSag : sagInput;
  if (usesSagPreset) {
    warnings.push(`SAG-Preset für ${bikeTypeName}: ${defaultSag} % wurde verwendet.`);
  }
  if (sagPercent <= 0 || sagPercent >= 80) {
    errors.push("SAG (%) muss zwischen 1 und 79 liegen.");
  } else if (sagPercent < 15 || sagPercent > 40) {
    warnings.push("SAG außerhalb des üblichen Bereichs (15–40%).");
  }
  if (errors.length > 0) {
    showResult(`<strong>${errors.join(" ")}</strong>`, "error");
    return;
  }
  const S = sagPercent / 100;

  // Leverage
  let L;
  let leverageSource;
  let wheelSagMm;
  let shockSagMm;
  let expertAnalysis = null;

  if (useExpert) {
    expertAnalysis = analyzeExpertCurve(travelMm, strokeMm);
    errors.push(...expertAnalysis.errors);
    warnings.push(...expertAnalysis.warnings);

    if (expertAnalysis.errors.length === 0) {
      shockSagMm = strokeMm * S;
      const sagPoint = interpolateCurveAtShockPosition(expertAnalysis.integrated, shockSagMm);
      if (!sagPoint || !isValidNumber(sagPoint.shockMm) || sagPoint.shockMm <= 0) {
        errors.push("Der SAG-Punkt konnte aus der Ratio-Kurve nicht bestimmt werden.");
      } else {
        wheelSagMm = sagPoint.wheelMm;
        L = sagPoint.ratio;
        leverageSource = "Expertenkurve, lokale Ratio am SAG";
      }
    }
  } else if (calculationMode !== "bike" && isValidNumber(leverageCustom) && useAdvanced) {
    if (leverageCustom < 1 || leverageCustom > 5) {
      errors.push("Die durchschnittliche Leverage Ratio muss zwischen 1.0 und 5.0 liegen.");
    } else {
      L = leverageCustom;
      leverageSource = "manuell eingegebener Durchschnitt";
      if (leverageCustom < 1.5 || leverageCustom > 3.5) {
        warnings.push("Durchschnittliche Leverage Ratio außerhalb typischer Werte (1.5–3.5).");
      }
    }
  } else {
    L = travelMm / strokeMm;
    leverageSource = calculationMode === "bike"
      ? "gesperrtem Bike-Preset aus Federweg ÷ Hub"
      : "Durchschnitt aus Federweg ÷ Hub";
    if (L < 1.5 || L > 3.5) {
      warnings.push("Berechnete durchschnittliche Leverage Ratio außerhalb typischer Werte (1.5–3.5).");
    }
  }

  if (!useExpert && isValidNumber(L) && L > 0) {
    shockSagMm = strokeMm * S;
    wheelSagMm = shockSagMm * L;
  }
  if (errors.length === 0 && (!isValidNumber(L) || L <= 0)) {
    errors.push("Leverage Ratio konnte nicht berechnet werden.");
  }
  if (errors.length === 0 && (!isValidNumber(shockSagMm) || shockSagMm <= 0)) {
    errors.push("Der Dämpfer-SAG konnte nicht berechnet werden.");
  }
  if (errors.length > 0) {
    showResult(`<strong>${errors.join(" ")}</strong>`, "error");
    return;
  }

  // Effektive Hinterradlast: Waagen > manuelle Geometrie > Bike-Geometrie > kalibrierter Fallback
  let B;
  let rearEquivalentMassKg;
  let cgFromRearMm = null;
  let cgFromFrontMm = null;
  let loadSource;
  let usedScaleMeasurement = false;
  const hasFrontScale = isValidNumber(frontScaleKg);
  const hasRearScale = isValidNumber(rearScaleKg);
  const hasWheelbase = isValidNumber(wheelbaseMm);
  const hasChainstay = isValidNumber(chainstayMm);
  const hasManualGeometry = hasWheelbase && hasChainstay;

  if (useAdvanced && hasFrontScale !== hasRearScale) {
    errors.push("Bitte Waage vorne und Waage hinten gemeinsam ausfüllen.");
  }
  if (useAdvanced && !hasFrontScale && !hasRearScale && hasWheelbase !== hasChainstay) {
    errors.push("Für die Geometrieschätzung werden Radstand und Rear Center gemeinsam benötigt.");
  }

  if (useAdvanced && hasFrontScale && hasRearScale) {
    if (frontScaleKg < 1 || frontScaleKg > 250 || rearScaleKg < 1 || rearScaleKg > 250) {
      errors.push("Die Waagenwerte müssen jeweils zwischen 1 und 250 kg liegen.");
    } else {
      const totalScaleKg = frontScaleKg + rearScaleKg;
      B = rearScaleKg / totalScaleKg;
      rearEquivalentMassKg = rearScaleKg;
      usedScaleMeasurement = true;
      loadSource = "Messung der Hinterradlast mit zwei Waagen";
      const scaleDifferencePercent = Math.abs(totalScaleKg - totalWeightKg) / totalWeightKg * 100;
      if (scaleDifferencePercent > 5) {
        warnings.push("Die Summe der Waagenwerte weicht um mehr als 5 % vom eingegebenen Gesamtgewicht ab.");
      }
      if (hasWheelbase && wheelbaseMm >= 800 && wheelbaseMm <= 1700) {
        cgFromFrontMm = wheelbaseMm * B;
        cgFromRearMm = wheelbaseMm * (1 - B);
      }
    }
  } else {
    let geometry = null;
    let geometrySource = null;

    if (calculationMode === "bike" && activeBikeGeometry) {
      geometry = {
        wheelbase: activeBikeGeometry.wheelbase,
        rearCenter: activeBikeGeometry.rearCenter,
        bbDrop: activeBikeGeometry.bbDrop || 0
      };
      geometrySource = `Geometriemodell des Bike-Presets, Größe ${activeBikeGeometry.size}`;
    } else if (calculationMode !== "bike" && useAdvanced && hasManualGeometry) {
      geometry = {
        wheelbase: wheelbaseMm,
        rearCenter: chainstayMm,
        bbDrop: isValidNumber(bbDropInput) ? bbDropInput : 0
      };
      geometrySource = "Geometrieschätzung aus manuellen Maßen";
    }

    if (geometry) {
      if (geometry.wheelbase < 800 || geometry.wheelbase > 1700) {
        errors.push("Der Radstand muss zwischen 800 und 1700 mm liegen.");
      }
      if (geometry.rearCenter < 300 || geometry.rearCenter > 650) {
        errors.push("Der Rear Center muss zwischen 300 und 650 mm liegen.");
      }
      if (Math.abs(geometry.bbDrop) >= geometry.rearCenter) {
        errors.push("Der BB-Drop muss kleiner als der Rear Center sein.");
      }

      if (errors.length === 0) {
        const geometryLoad = calculateGeometryLoad(
          weightKg,
          bikeWeightKg,
          geometry.wheelbase,
          geometry.rearCenter,
          geometry.bbDrop,
          riderOffsetMm
        );
        B = geometryLoad.rearShare;
        rearEquivalentMassKg = geometryLoad.rearEquivalentMassKg;
        cgFromRearMm = geometryLoad.cgFromRearMm;
        cgFromFrontMm = geometryLoad.cgFromFrontMm;
        loadSource = `${geometrySource} und ${Math.round(riderOffsetMm)} mm effektivem Fahrer-Offset`;
      }
    } else {
      B = {
        xc: 0.57,
        trail: 0.58,
        enduro: 0.58,
        dh: 0.59,
        custom: 0.58
      }[bikeType];
      rearEquivalentMassKg = totalWeightKg * B;
      loadSource = `kalibriertem ${bikeTypeName}-Fallback`;
    }
  }

  if (!isValidNumber(B) || !isValidNumber(rearEquivalentMassKg)) {
    errors.push("Hinterradlast konnte nicht bestimmt werden.");
  } else if (B < 0.45 || B > 0.75) {
    warnings.push("Die effektive Hinterradlast liegt außerhalb des erwarteten Bereichs von 45–75 %.");
  }
  if (errors.length > 0) {
    showResult(`<strong>${errors.join(" ")}</strong>`, "error");
    return;
  }

  // Physikalische Umrechnung: kg -> N und mm -> N/mm
  const g = 9.80665;
  const effectiveSpringCompressionMm = shockSagMm + preloadMm;
  const rearForceN = rearEquivalentMassKg * g;
  const rateNmm = rearForceN * L / effectiveSpringCompressionMm;
  const rateLbInRaw = rateNmm * 5.710147;

  const rounded25 = Math.round(rateLbInRaw / 25) * 25;
  const softer25 = Math.max(25, rounded25 - 25);
  const firmer25 = rounded25 + 25;
  const cgHtml = isValidNumber(cgFromRearMm)
    ? `<p class="result-guidance"><strong>Lastmodell:</strong> ${loadSource}. Effektiver Gesamtschwerpunkt ca. ${Math.round(cgFromRearMm)} mm vor der Hinterachse (${Math.round(cgFromFrontMm)} mm hinter der Vorderachse).</p>`
    : "";
  const warningHtml = warnings.length
    ? `<ul class="messages">${warnings.map((warning) => `<li><small>${warning}</small></li>`).join("")}</ul>`
    : "";
  const resultMetricsHtml = `
    <div class="result-metrics">
      <div class="result-metric">
        <span>Hinterradweg bei SAG</span>
        <strong>${formatValue(wheelSagMm, 1)} mm</strong>
      </div>
      <div class="result-metric">
        <span>Dämpfer-SAG</span>
        <strong>${formatValue(shockSagMm, 2)} mm</strong>
      </div>
      <div class="result-metric">
        <span>${useExpert ? "Lokale Ratio am SAG" : "Durchschnittliche Ratio"}</span>
        <strong>${formatValue(L, 3)}</strong>
      </div>
      <div class="result-metric">
        <span>Effektive Hinterradlast</span>
        <strong>${formatValue(B * 100, 1)} %</strong>
      </div>
    </div>`;
  const calculationNote = useExpert
    ? expertCurveSource === "average"
      ? `Die Expertenansicht verwendet derzeit nur eine konstante, aus dem Durchschnitt erzeugte Kurve. Für einen Genauigkeitsgewinn muss sie durch echte Punktdaten ersetzt werden.`
      : `SAG wird als Anteil des Dämpferhubs angesetzt; die integrierte Ratio-Kurve liefert Hinterradposition und lokale Ratio an genau diesem Punkt.`
    : `Näherungswert mit ${leverageSource} und als konstant angenommener Übersetzung.`;

  let qualityLabel;
  if (useExpert && expertCurveSource !== "average") {
    qualityLabel = "Expertenmodus · eingegebene Ratio-Punktkurve";
  } else if (useExpert) {
    qualityLabel = "Expertenansicht · noch konstante Durchschnittskurve";
  } else if (calculationMode === "bike") {
    qualityLabel = usedScaleMeasurement
      ? "Bike-Preset · gemessene Radlast"
      : activeBikeGeometry
      ? `Bike-Preset · größenabhängiges Geometriemodell`
      : activeBikePreset.evidence === "graph"
        ? "Bike-Preset · Herstellermaße; Kurvendiagramm noch nicht digitalisiert"
        : `Bike-Preset · ${activeBikePreset.evidenceLabel}`;
  } else if (calculationMode === "quick") {
    qualityLabel = "Schnellcheck · grobe Orientierung";
  } else {
    qualityLabel = "Standard · durchschnittliche Leverage Ratio";
  }

  const refineLabel = calculationMode === "bike" && activeBikePreset && activeBikePreset.evidence === "graph"
    ? "Herstellerdiagramm verfügbar: mit Ratio-Kurve verfeinern"
    : "Mit eigener Ratio-Kurve verfeinern";
  const refineHtml = !useExpert
    ? `<button type="button" class="result-expert-button" onclick="openExpertMode('result')">${refineLabel}</button>`
    : "";

  showResult(`
    <div class="result-quality"><span class="quality-badge">${qualityLabel}</span></div>
    <div class="result-headline">
      <div>
        <span class="result-kicker">EMPFOHLENER STARTWERT</span>
        <div class="result-rate">${rounded25} <span>lb/in</span></div>
      </div>
      <div class="result-raw">
        Berechnet: ${Math.round(rateLbInRaw)} lb/in<br>
        ${rateNmm.toFixed(1)} N/mm
      </div>
    </div>
    ${resultMetricsHtml}
    <div class="spring-window" aria-label="Abstimmungsfenster der Federrate">
      <div class="spring-option">
        <span>Softer · mehr Traktion</span>
        <strong>${softer25} lb/in</strong>
      </div>
      <div class="spring-option recommended">
        <span>Empfohlener Start</span>
        <strong>${rounded25} lb/in</strong>
      </div>
      <div class="spring-option">
        <span>Straffer · mehr Support</span>
        <strong>${firmer25} lb/in</strong>
      </div>
    </div>
    <p class="result-guidance">Der Startwert ist die technisch nächstliegende 25-lb/in-Stufe. Vorspannung verändert den SAG, nicht die eigentliche Federsteifigkeit. Verwende sie nur innerhalb der Vorgaben des Dämpferherstellers.</p>
    ${cgHtml}${warningHtml}
    <small class="calculation-note">${calculationNote} Lastbasis: ${loadSource}. ${preloadMm > 0 ? `Berücksichtigte wirksame Vorspannung: ${formatValue(preloadMm, 1)} mm. ` : ""}Ungefederte Massen bleiben näherungsweise berücksichtigt; Herstellerangaben und Praxistest haben Vorrang.</small>
    ${refineHtml}
  `);
}
